"use client";

import * as React from "react";

import { getInsightOutbox } from "../services/home-insights-outbox";
import { sectionVisibility } from "./home-section-visibility";
import type { AppConfig } from "../config/get-config";

type Props = { runtimeConfig: AppConfig | null };
type SectionState = {
  key: string;
  order: number;
  ratio: number;
  reached: boolean;
  reachCandidateAt: number | null;
  activeMs: number;
  interactionCount: number;
  maxVisiblePercent: number;
};

const PAGE_VERSION = "home_v2";
const VISITOR_STORAGE_KEY = "pteultra_home_visitor_v1";
const VISITOR_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const ACTIVITY_WINDOW_MS = 60_000;

function createId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getVisitorId() {
  const now = Date.now();
  try {
    const stored = JSON.parse(localStorage.getItem(VISITOR_STORAGE_KEY) ?? "null") as
      | { id?: string; expiresAt?: number }
      | null;
    if (stored?.id && typeof stored.expiresAt === "number" && stored.expiresAt > now) {
      return stored.id;
    }
  } catch {
    // Rotate when storage is blocked or an older value is unreadable.
  }

  const id = createId();
  try {
    localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify({ id, expiresAt: now + VISITOR_TTL_MS }));
  } catch {
    // Analytics remains best effort when storage is unavailable.
  }
  return id;
}

export default function HomePageInsightsTracker({ runtimeConfig }: Props) {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production" || window.location.pathname !== "/") return;

    const endpoint = runtimeConfig?.apiUrl
      ? `${runtimeConfig.apiUrl.replace(/\/+$/, "")}/Analytics/home-page-insights`
      : "";
    if (!endpoint) return;

    const pageContext = window.__ptePageView ?? (window.__ptePageView = {
      pageViewId: createId(), startedAtUtc: new Date().toISOString(),
    });
    const { startedAtUtc, pageViewId } = pageContext;
    const visitorId = getVisitorId();
    const sections = new Map<HTMLElement, SectionState>();
    const ctaCounts = new Map<string, number>();
    let dirty = false;
    const outbox = getInsightOutbox(endpoint, {
      "Content-Type": "application/json",
      ...(runtimeConfig?.apiKey ? { apikey: runtimeConfig.apiKey } : {}),
    }, () => {
      if (window.pteLandingTracking) window.pteLandingTracking.record('render_error', { errorCode: 'insights_rejected' });
      else {
        const errors = window.__pteErrorBuffer;
        if (errors && errors.length < 3 && !errors.includes('insights_rejected')) errors.push('insights_rejected');
      }
    });
    let lastActivityAt = Date.now();
    let lastTickAt = performance.now();
    let totalActiveMs = 0;
    let maxScrollPercent = 0;

    document.querySelectorAll<HTMLElement>("[data-home-section-key]").forEach((element) => {
      const key = element.dataset.homeSectionKey;
      const order = Number(element.dataset.homeSectionOrder);
      if (key && Number.isInteger(order)) {
        sections.set(element, {
          key,
          order,
          ratio: 0,
          reached: false,
          reachCandidateAt: null,
          activeMs: 0,
          interactionCount: 0,
          maxVisiblePercent: 0,
        });
      }
    });

    const tick = () => {
      const now = performance.now();
      const elapsed = Math.min(now - lastTickAt, 1_000);
      lastTickAt = now;
      const active = document.visibilityState === "visible"
        && document.hasFocus()
        && Date.now() - lastActivityAt <= ACTIVITY_WINDOW_MS;

      let anyActiveSection = false;
      sections.forEach((state, element) => {
        state.ratio = sectionVisibility(element.getBoundingClientRect(), window.innerWidth, window.innerHeight);
        state.maxVisiblePercent = Math.max(state.maxVisiblePercent, Math.round(state.ratio * 100));
        state.reachCandidateAt = active && state.ratio >= 0.25 ? (state.reachCandidateAt ?? now) : null;
        if (!state.reached && state.reachCandidateAt !== null && now - state.reachCandidateAt >= 1_000) {
          state.reached = true;
          dirty = true;
        }
        if (active && state.ratio >= 0.5) {
          state.activeMs += elapsed;
          anyActiveSection = true;
          dirty = true;
        }
      });
      if (anyActiveSection) totalActiveMs += elapsed;
    };

    const recordActivity = () => { lastActivityAt = Date.now(); };
    const updateScroll = () => {
      recordActivity();
      const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const percentage = height <= window.innerHeight
        ? 100
        : Math.round(((window.scrollY + window.innerHeight) / height) * 100);
      maxScrollPercent = Math.max(maxScrollPercent, Math.min(100, percentage));
      dirty = true;
    };

    const flush = (force = false, exiting = false) => {
      if (!dirty && !force) { void outbox.flush(); return; }
      const reachedOrders = [...sections.values()]
        .filter((section) => section.reached)
        .map((section) => section.order);
      const payload = {
        pageViewId,
        visitorId,
        pageVersion: PAGE_VERSION,
        path: "/",
        startedAtUtc,
        lastActivityAtUtc: new Date().toISOString(),
        maxScrollPercent,
        totalActiveMs: Math.round(totalActiveMs),
        sections: [...sections.values()]
          .sort((a, b) => a.order - b.order)
          .map((section) => ({
            sectionKey: section.key,
            reached: section.reached,
            activeMs: Math.round(section.activeMs),
            maxVisiblePercent: section.maxVisiblePercent,
            interactionCount: section.interactionCount,
            continuedToNext: reachedOrders.some((order) => order > section.order),
          })),
        ctas: [...ctaCounts].map(([ctaKey, clickCount]) => ({ ctaKey, clickCount })),
      };

      dirty = false;
      outbox.enqueue(payload, exiting);
    };

    const onClick = (event: MouseEvent) => {
      recordActivity();
      const target = event.target;
      if (!(target instanceof Element)) return;

      const sectionElement = target.closest<HTMLElement>("[data-home-section-key]");
      const section = sectionElement ? sections.get(sectionElement) : undefined;
      if (section) {
        section.interactionCount += 1;
        dirty = true;
      }

      const cta = target.closest<HTMLElement>("[data-home-cta-key]");
      const key = cta?.dataset.homeCtaKey;
      if (key) {
        ctaCounts.set(key, (ctaCounts.get(key) ?? 0) + 1);
        dirty = true;
        void flush(true);
      }
    };

    const onSpeakingOutcome = (event: Event) => {
      const key = (event as CustomEvent<string>).detail;
      if (!["speaking_recording_started"].includes(key)) return;
      ctaCounts.set(key, (ctaCounts.get(key) ?? 0) + 1);
      dirty = true;
      void flush(true);
    };
    window.addEventListener("home-speaking-outcome", onSpeakingOutcome);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") void flush(true, true);
    };
    const onPageHide = () => { void flush(true, true); };
    const tickTimer = window.setInterval(tick, 500);
    const flushTimer = window.setInterval(() => void flush(), 60_000);

    document.addEventListener("click", onClick, { capture: true });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("pointerdown", recordActivity, { passive: true });
    window.addEventListener("keydown", recordActivity);
    updateScroll();
    void flush(true);

    return () => {
      tick();
      void flush(true, true);

      window.clearInterval(tickTimer);
      window.clearInterval(flushTimer);
      document.removeEventListener("click", onClick, { capture: true });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("home-speaking-outcome", onSpeakingOutcome);
      window.removeEventListener("scroll", updateScroll);
      window.removeEventListener("pointerdown", recordActivity);
      window.removeEventListener("keydown", recordActivity);
    };
  }, [runtimeConfig]);

  return null;
}
